// @ts-nocheck
import React, { FC, useEffect, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';

import { Container, Row, Col } from 'crehana-ui/GridV2';

import isFuture from 'date-fns/is_future';

import createRecursiveFunctionForAnalytics from 'crehana-utils/createRecursiveFunctionForAnalytics';

import useIsClient from 'crehana-react-hooks/useIsClient';

import { LightMenuWithTopBannerAndAuthDialog as LightMenu } from '@crehana/web';

import { InitialStateResult } from 'Jsx/Utils/getBaseInitialState';

import RETARGETTINGS_MUTATION from 'Jsx/CheckoutV4/Graphql/Mutation/AddCheckoutRetargeting.graphql';

import {
  HeroSection,
  FAQSection,
  MembershipPlans,
  BenefitsSection,
  FeaturesSection,
  MentorsSection,
} from './sections';

import { FixedCallToAction, ContactForm } from './components';

import GET_BANNER_QUERY from './graphql/getBanner.graphql';
import { getBanner, getBannerVariables } from './graphql/types/getBanner';

import { InitialState, Membership } from './types';

const sendDataToAnalytics = (memberships: Partial<Membership>[]) => {
  createRecursiveFunctionForAnalytics(() => {
    if (window.gtag) {
      const promotions = memberships.map(
        ({ originalId, title }: { originalId?: number; title?: string }) => ({
          id: `MEMBERSHIP_${originalId}`,
          name: title,
        }),
      );

      window.gtag('event', 'view_promotion', {
        send_to: 'UA-60829200-1',
        promotions,
      });
    }
  })();
};

const scrollToSection = (sectionId: string): void => {
  const section = document.getElementById(sectionId);

  if (section) {
    window.scrollTo({
      top: section.offsetTop,
      behavior: 'smooth',
    });
  }
};

export type ExtraState = {};

export type AppProps = InitialState;

const App: FC<InitialStateResult<AppProps & ExtraState>> = ({
  countryPrefix,
  membershipsSection,
  categoryId,
  heroSection,
  featuresSection,
  faqSection,
  mentorsSection,
}) => {
  const isClient = useIsClient();

  const [getBannerQuery, { data }] =
    useLazyQuery<getBanner, getBannerVariables>(GET_BANNER_QUERY);
  const [banner, setBanner] =
    useState<{
      message: string;
      alternativeShortMessage: string;
      expirationDate: Date;
    } | null>(null);
  const [retargetingMutation] = useMutation(RETARGETTINGS_MUTATION);

  useEffect(() => {
    const defaultItemId = 20;

    retargetingMutation({
      variables: {
        itemId: defaultItemId,
        itemType: 'membership',
        eventType: 'membership',
      },
    });

    sendDataToAnalytics(membershipsSection.memberships);
  }, []);

  useEffect(() => {
    getBannerQuery({
      variables: { identifiers: ['membership_landing'] },
    });
  }, []);

  useEffect(() => {
    if (data) {
      const { banners } = data;

      if (banners) {
        if (banners.length === 1) {
          const _banner = banners[0];

          if (_banner && isFuture(new Date(_banner.dueDate))) {
            setBanner({
              message: _banner.title,
              alternativeShortMessage: _banner.contentMobile || _banner.title,
              expirationDate: new Date(_banner.dueDate),
            });
          }
        }
      }
    }
  }, [data]);

  const handleScrollTo = () => {
    scrollToSection('membershipv2-plans');
  };

  return (
    <div>
      <LightMenu
        isTransparent
        theme="dark"
        position="absolute"
        layoutConfig={{
          hidePremiumLandingLink: true,
        }}
      />
      <HeroSection
        {...heroSection}
        handleClickOnPrimaryButton={handleScrollTo}
        remainingOfferTime={banner?.expirationDate}
      />
      <FeaturesSection {...featuresSection} />
      <MentorsSection {...mentorsSection} />
      <MembershipPlans {...membershipsSection} banner={banner} />
      <BenefitsSection memberships={membershipsSection.memberships} />
      <FAQSection {...faqSection} />
      {isClient && (
        <FixedCallToAction
          scrollToSpecifiedSection={() => {
            scrollToSection('membershipv2-plans');
          }}
          banner={banner}
          buttonLabel={heroSection.buttonLabel}
        />
      )}
    </div>
  );
};

export default App;
